/* Free-shipping reminder pop-up. Markup: snippets/ms-free-shipping-reminder.liquid */
(function () {
  if (customElements.get("ms-free-shipping-reminder")) return;

  const CART_UPDATED = (typeof CART_EVENT !== "undefined" && CART_EVENT.cartUpdate) || "maximize:cart:updated";
  const DISMISS_KEY = "ms-fsr-dismissed";
  const AUTO_HIDE_SCREENS = 2;
  // Other bottom overlays the reminder must not compete with: the consent gate, Shopify's
  // privacy banner and HubSpot's newsletter CTA (injected via GTM, z-index 9999).
  const OVERLAY_SELECTORS = [
    "#ms-consent-gate",
    "#shopify-pc__banner",
    '#hs-web-interactives-bottom-anchor [id^="hs-overlay-cta"]',
  ];
  const HUBSPOT_ANCHOR_ID = "hs-web-interactives-bottom-anchor";

  const storage = {
    get(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.sessionStorage.setItem(key, value);
      } catch (e) {
        /* private mode: dismissal lasts for this page only */
      }
    },
  };

  function isShown(el) {
    return !!el && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== "hidden";
  }

  class MsFreeShippingReminder extends HTMLElement {
    connectedCallback() {
      this.threshold = Number(this.dataset.threshold);
      if (!this.threshold) return;

      this.total = Number(this.dataset.total) || 0;
      this.count = Number(this.dataset.count) || 0;
      this.dismissed = storage.get(DISMISS_KEY) === "1";
      this.autoHidden = false;
      this.drawerOpen = false;
      this.scrollAnchor = window.scrollY;
      this.messageEl = this.querySelector("[data-fsr-message]");
      this.progressEl = this.querySelector("[data-fsr-progress]");
      this.barEl = this.querySelector("[data-fsr-bar]");
      this.messageTemplate = this.messageEl.innerHTML;
      this.initialAmountText = (this.messageEl.querySelector(".amount-to-free-shipping") || {}).textContent;

      this.onScroll = this.onScroll.bind(this);
      this.onCartUpdated = this.onCartUpdated.bind(this);
      this.onDrawerOpen = () => {
        this.drawerOpen = true;
        this.render();
      };
      this.onDrawerClose = () => {
        this.drawerOpen = false;
        this.render();
      };

      this.querySelector("[data-fsr-close]").addEventListener("click", () => this.dismiss());
      document.addEventListener(CART_UPDATED, this.onCartUpdated);
      document.addEventListener("cart:minicart:open", this.onDrawerOpen);
      document.addEventListener("cart:minicart:close", this.onDrawerClose);
      window.addEventListener("scroll", this.onScroll, { passive: true });
      window.addEventListener("resize", this.onScroll, { passive: true });

      // The consent gate, the privacy banner and HubSpot's anchor are injected/removed at body level.
      this.overlayObserver = new MutationObserver(() => this.render());
      this.overlayObserver.observe(document.body, { childList: true });
      // Fired when the buyer answers the banner; it may hide a moment later, so re-check after it has.
      this.onConsent = () => setTimeout(() => this.render(), 400);
      document.addEventListener("visitorConsentCollected", this.onConsent);

      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener(CART_UPDATED, this.onCartUpdated);
      document.removeEventListener("cart:minicart:open", this.onDrawerOpen);
      document.removeEventListener("cart:minicart:close", this.onDrawerClose);
      window.removeEventListener("scroll", this.onScroll);
      window.removeEventListener("resize", this.onScroll);
      document.removeEventListener("visitorConsentCollected", this.onConsent);
      if (this.overlayObserver) this.overlayObserver.disconnect();
      if (this.hubspotObserver) this.hubspotObserver.disconnect();
    }

    get remaining() {
      return Math.max(0, this.threshold - this.total);
    }

    shouldShow() {
      return (
        this.count > 0 &&
        this.remaining > 0 &&
        !this.dismissed &&
        !this.autoHidden &&
        !this.drawerOpen &&
        !OVERLAY_SELECTORS.some((selector) => isShown(document.querySelector(selector)))
      );
    }

    // HubSpot opens and closes its CTA inside its own anchor, which the body observer can't see.
    watchHubspot() {
      if (this.hubspotObserver) return;
      const anchor = document.getElementById(HUBSPOT_ANCHOR_ID);
      if (!anchor) return;
      this.hubspotObserver = new MutationObserver(() => this.render());
      this.hubspotObserver.observe(anchor, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["style", "class"],
      });
    }

    render() {
      this.watchHubspot();
      const show = this.shouldShow();
      if (show && this.hidden) this.scrollAnchor = window.scrollY;
      this.hidden = !show;
      if (show) this.updateOffset();
    }

    update() {
      const percent = Math.min(100, Math.round((this.total / this.threshold) * 100));
      const amount = this.formatAmount(this.remaining);
      const amountEl = this.messageEl.querySelector(".amount-to-free-shipping");
      if (amountEl) {
        amountEl.textContent = amount;
      } else {
        this.messageEl.innerHTML = this.messageTemplate;
      }
      this.barEl.style.width = percent + "%";
      this.progressEl.setAttribute("aria-valuenow", String(percent));
    }

    formatAmount(cents) {
      const format = window.Maximize && window.Maximize.currencyFormat;
      if (typeof formatMoney === "function" && format) return formatMoney(cents, format);
      return this.initialAmountText || (cents / 100).toFixed(2);
    }

    onCartUpdated() {
      fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : "/") + "cart.js", {
        headers: { Accept: "application/json" },
      })
        .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
        .then((cart) => {
          this.total = cart.total_price;
          this.count = cart.item_count;
          // A cart change is new information, so a scroll-hidden reminder may return.
          this.autoHidden = false;
          this.update();
          this.render();
        })
        .catch(() => {
          /* keep the last known state */
        });
    }

    onScroll() {
      if (this.hidden) return;
      if (Math.abs(window.scrollY - this.scrollAnchor) > window.innerHeight * AUTO_HIDE_SCREENS) {
        this.autoHidden = true;
        this.render();
        return;
      }
      if (!this.offsetFrame) {
        this.offsetFrame = requestAnimationFrame(() => {
          this.offsetFrame = null;
          this.updateOffset();
        });
      }
    }

    // Sit above the sticky add-to-cart bar instead of covering it.
    updateOffset() {
      const sticky = document.querySelector(".sticky-add-to-cart__wrapper");
      let offset = 0;
      if (isShown(sticky)) {
        const rect = sticky.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          offset = Math.max(0, window.innerHeight - rect.top);
        }
      }
      this.style.setProperty("--ms-fsr-offset", offset + "px");
    }

    dismiss() {
      this.dismissed = true;
      storage.set(DISMISS_KEY, "1");
      this.render();
    }
  }

  customElements.define("ms-free-shipping-reminder", MsFreeShippingReminder);
})();
